import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Invoice } from '../shared/models/invoice';
import { PpsUtilityService } from '../services/pps-utility.service';
import { ImageService } from '../services/image.service';
import { ProjectInvoiceActionComponent } from './project-invoice-action.component';
import { ProjectInvoiceHeaderComponent } from './project-invoice-header.component';
import { ProjectService } from '../services/project.service';
import { ToastService } from '../shared/toast/toast.service';
import { Project } from '../shared/models/project';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { format, formatISO, isValid, parse } from 'date-fns';
import { InvoiceService } from '../services/invoice.service';
import * as _ from 'lodash-es';
import { SubSink } from 'subsink';

@Component({
  selector: 'pps-project-invoice-list',
  templateUrl: './project-invoice-list.component.html',
  styles: [`
    #project-invoice .ag-theme-alpine .ag-root-wrapper{
      border-radius: 0px !important;
    }
  `],
  encapsulation: ViewEncapsulation.None
})
export class ProjectInvoiceListComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  private project: Project;
  private projectId: number;
  private parent:any = null;
  private topApi: any;
  private bottomApi: any;

  invoices: Invoice[] = [];
  invoiceTotal: Invoice[] = [];
  gridHeaders: any = [];
  useAsModalDialog: boolean = true;
  context:any = {};

  topOptions: any = {
    alignedGrids: () => this.bottomApi ? [this.bottomApi] : [],
    components: {
      projectInvoiceHeaderComponent: ProjectInvoiceHeaderComponent
    },
    getRowId: param => `${param.data.id}`,
    suppressHorizontalScroll: true,
    defaultColDef: {
      resizable: true
    }
  };

  bottomOptions: any = {
    alignedGrids: () => this.topApi ? [this.topApi] : [],
    components: {
      projectInvoiceHeaderComponent: ProjectInvoiceHeaderComponent
    }
  };
  
  constructor(
    private _dialogRef: DynamicDialogRef,
    private _dialogConfig: DynamicDialogConfig,
    private util: PpsUtilityService,
    private imageService: ImageService,
    private _confirm: ConfirmationService,
    private projectService: ProjectService,
    private invoiceService: InvoiceService,
    private toast: ToastService
  ) {
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.parent = this._dialogConfig.data.parent;
    this.context = {componentParent: this};
    this.gridHeaders = this.getColumnDefs();

    this.setData(this._dialogConfig.data.project);
  }

  setData(project: Project){
    this.project = _.cloneDeep(project);

    this.invoices = Array.isArray(this.project.invoices) ? this.project.invoices : [];
    let total: number = 0;
    this.invoices.forEach(inv => total += inv.invoiceValue);

    this.invoiceTotal = [{
      id:0,
      invoiceNo: "Total",
      invoiceValue: total,
      invoiceDate: project.poNumber ? "PO#:  " + project.poNumber : null,
      incomeMonth: project.poAmount ? this.util.formatCurrency(project.poAmount) : null,
      memo: "",
      invoiceImageUrl: "",
      originalInvoiceFilename: ""
    }];
  }

  cancel(){
    this._dialogRef.close();
  }

  onTopGridReady(params) {
    this.topApi = params.api;
  }

  onBottomGridReady(params) {
    this.bottomApi = params.api;
  }

  showInvoiceImage(invoice){
    this.imageService.showInvoiceImage(invoice, () => {});
  }

  deleteInvoice(invoice: Invoice){
    this._confirm.confirm({
      header: "Delete Invoice",
      message: `Are you sure that you want to delete invoice: ${invoice.invoiceNo}?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => {
        this.subs.sink = this.projectService.deleteProjectInvoice(this.project.id, invoice.id)
          .subscribe(res => {
            if (res.succeed){
              this.toast.succeeded(`Invoice ${invoice.invoiceNo} has been deleted`);
              this.invoices = this.invoices.filter(i => i.id !== invoice.id);
              this.parent.resetInvoice(this.project.id, this.invoices);

              this.invoiceTotal[0].invoiceValue = 0;
              this.invoices.forEach(inv => this.invoiceTotal[0].invoiceValue += inv.invoiceValue);
              this.bottomApi.setGridOption('rowData', this.invoiceTotal);
            }else{
              this.toast.failed(`Failed to delete invoice ${invoice.invoiceNo}`);
            }
          });
      }});
  }

  getColumnDefs(){
    let canUpdate = this.parent.canUpdateInvoice();
    
    return [
      {
        headerName: 'Invoice No',
        field:'invoiceNo',
        filter: true,
        sortable: true,
        resizable: true,
        width: 100,
        editable: this.isCellEditable,
        onCellValueChanged: this.invoiceChanged,
        headerTooltip: canUpdate ? 'Double click to edit invoice no' : '',
        headerComponent: 'projectInvoiceHeaderComponent',
        headerComponentParams: {
          iconClass: canUpdate ? 'fas fa-edit p-mr-2' : ''
        }
      },
      {
        headerName: 'Value',
        field:'invoiceValue',
        filter: true,
        sortable: true,
        width: 100,
        cellStyle: this.util.getNumericCellStyle,
        valueFormatter: this.formatInvoiceValue,
        editable: this.isCellEditable,
        onCellValueChanged: this.invoiceChanged,
        headerTooltip: canUpdate ? 'Double click to edit invoice value' : '',
        headerComponent: 'projectInvoiceHeaderComponent',
        headerComponentParams: {
          iconClass: canUpdate ? 'fas fa-edit p-mr-2' : ''
        }
      },
      {
        headerName: 'Date',
        field:'invoiceDate',
        filter: true,
        sortable: true,
        comparator: (date1, date2) => this.util.dateComparator(date1, date2),
        width: 120,
        cellStyle: {textAlign: "center"},
        valueGetter: this.getDateValue,
        editable: this.isCellEditable,
        onCellValueChanged: this.invoiceChanged,
        valueParser: this.validateDateValue,
        headerTooltip: canUpdate ? 'Double click to edit invoice date' : '',
        headerComponent: 'projectInvoiceHeaderComponent',
        headerComponentParams: {
          iconClass: canUpdate ? 'fas fa-edit p-mr-2' : ''
        }
      },
      {
        headerName: 'Income Month',
        field:'incomeMonth',
        filter: true,
        sortable: true,
        comparator: (date1, date2) => this.util.dateComparator(date1, date2),
        width: 150,
        cellStyle: {textAlign: "center"},
        valueGetter: this.getDateValue,
        editable: this.isCellEditable,
        onCellValueChanged: this.invoiceChanged,
        valueParser: this.validateDateValue,
        headerTooltip: canUpdate ? 'Double click to edit income month' : '',
        headerComponent: 'projectInvoiceHeaderComponent',
        headerComponentParams: {
          iconClass: canUpdate ? 'fas fa-edit p-mr-2' : ''
        }
      },
      {
        headerName: "Invoice",
        field: "value",
        cellRenderer: ProjectInvoiceActionComponent,
        colId: "params",
        cellStyle: {textAlign: "center"},
        width: 220
      }
    ];
  }

  private getDateValue(params){
    let val = params.data[params.colDef.field];
    if (params.data.id === 0){ // Total line;
      return val; 
    }

    if (typeof val === 'string' || val instanceof String){
      val = new Date(val as string);
    }

    if (val && typeof val.getMonth === 'function' ){
      let value = params.context.componentParent.util.getDateString(val);
      return value;
    }

    return val;
  }

  private validateDateValue(params){
    let newValue = params.newValue;
    let oldValue = params.oldValue;
    let invalidDate = false;

    try{
      if (!isValid(parse(newValue, "dd/MM/yyyy", new Date()))){
        invalidDate = true;
      }
    }catch(e){
      invalidDate = true;
    }

    if (invalidDate){
      params.api.stopEditing(true);

      params.context.componentParent._confirm.confirm({
          header: "Invalid Date",
          message: `Value entered [${newValue}] is not a valid date. Valid date format: dd/mm/yyyy.`,
          icon: "pi pi-question-circle",
          defaultFocus: "accept",
          key: "alert",
          accept: () => {
          }});

      return oldValue;
    }

    return parse(newValue, "dd/MM/yyyy", new Date());
  }

  private invoiceChanged(params){
    let _this = params.context.componentParent;
    let projectInvoice = _.cloneDeep(params.data);
    let columnName = "Invoice No";
    let invoiceValueUpdated = false;

    if (params.newValue != params.oldValue){
      if (params.column.colId === "invoiceValue"){
        columnName = "Invoice Value";
        invoiceValueUpdated = true;
      }

      projectInvoice.invoiceValue = +projectInvoice.invoiceValue;
      projectInvoice.invoiceDate = new Date(projectInvoice.invoiceDate);
      projectInvoice.incomeMonth = new Date(projectInvoice.incomeMonth);

      if (params.column.colId === "invoiceDate"){
        columnName = "Invoice Date";
      }

      if (params.column.colId === "incomeMonth"){
        columnName = "Income Month";
      }
  
      _this.subs.sink = _this.invoiceService.updateInvoice(_.cloneDeep(projectInvoice)).subscribe(res => {
        if (res.succeed){
          _this.updateInvoiceTotal(projectInvoice);
          _this.toast.succeeded(`${columnName} updated successfully.`);
        }else{
          _this.toast.failed(`Failed to update ${columnName}: ${res.errors.join(";")}`);
        }
      });
    }
  }

  private formatInvoiceValue(params){
    var invoiceValue = params.context.componentParent.util.formatCurrency(+params.data.invoiceValue);
    return invoiceValue;
  }

  private isCellEditable(params){
    let canUpdate = params.context.componentParent.parent.canUpdateInvoice();

    let invoice = params.data;
    if (canUpdate && invoice.id !== 0){
      return true;
    }

    return false;
  }

  private updateInvoiceTotal(invoice: Invoice){
    var total: number = 0;
    var idx = this.invoices.findIndex(i => i.id === invoice.id);
    if (idx !== -1){
      this.invoices[idx] = invoice;
    }

    this.invoices.forEach(inv => total += inv.invoiceValue);
    this.invoiceTotal[0].invoiceValue = total;
    this.parent.resetInvoice(this.project.id, this.invoices);
    this.bottomApi.setGridOption('rowData', this.invoiceTotal);
  }
}
